import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {DetectMethodChanges, DetectSetterChanges, getStrikeStep} from '../utils';
import {OptionExpirationDescriptor} from '../shell-communication/shell-dto-protocol';
import {TradingInstrument} from '../trading-instruments/trading-instrument.class';
import {
    isFollowThroughOnly,
    OPG_RELATIVE_RISK_LEVELS,
    OpgAtmAnchor,
    OpgAtmOffsetSub,
    OpgAtmRangeValue,
    OpgMaxRisk,
    OpgPriceRange,
    OpgRelativeRiskLevel,
    OpgSide,
    SideInputs
} from './opg-copilot-domain';
import {isPresetAnchorValid, isSideComplete, snapToStrikeStep} from './opg-copilot-questionnaire';
import {convertOnNormalizedModeFlip} from './normalized-raw-conversion';
import {ParamChangeMap} from './opg-copilot-param-diff';

// ONE side's parameter block of the OPG CoPilot questionnaire (CR-20260710-001, spec §5):
// Price Range (3 modes incl. the OPG-only ATM Offset) -> Minimum Profit -> Max Risk.
// Instantiated once on the Calls/Puts tabs and TWICE on Calls & Puts
// (calls-left / puts-right), where each instance is collapsible to a vertical rail
// (sketch frame 06). Identical internal layout in every instantiation: the selector row,
// then that mode's inputs on their OWN row (frame-02 pattern; owner confirmed 2026-07-10 —
// the wide tab never compresses selector+inputs onto one line).
//
// EXTRACTED TO copilot-shared (CR-20260804-001, owner ruling): this component now serves BOTH
// Co-Pilots - OPG and the HedgeMatrix Select tab - so the two questionnaires cannot drift.
// The old "isolated sibling - never imported across" convention is retired FOR THIS FILE by
// that ruling (it still governs the results/cards side of each Co-Pilot).
//
// Contract with the parent orchestrator:
//   - `inputs` is a WORKING SideInputs object OWNED BY THE PARENT; user edits mutate its
//     fields in place (mode switches REPLACE inputs.priceRange/maxRisk with a fresh
//     variant object — the domain unions are discriminated). `changed` fires after every
//     user-driven mutation; the parent gates RUN via isTabComplete. A switch INTO
//     follow-through-only coverage replaces maxRisk with NULL — the question ceases to
//     exist (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03); the local mirror
//     below is what restores the previous choice on the way back.
//   - Collapse is VISUAL ONLY (spec §5): a collapsed side keeps its inputs and still
//     gates the Run. The `collapsed` setter NEVER emits `collapsedChange` (echo-loop
//     rule); only the user-driven header/rail click does.
//   - DevExtreme value-changed handlers are guarded on $event.event: programmatic echoes
//     (hydration writing the local fields back through [(value)]) must not re-emit.
//
// §10 prohibitions honored here: risk levels render EXACTLY Low | Moderate | Aggressive |
// All (never High/Conservative/Auto) — the buttons derive from OPG_RELATIVE_RISK_LEVELS.
// Presence+sanity gating only (courier §6): validity gates the RUN, never edits. The
// per-block "— incomplete" legend hints were REMOVED at owner smoke 2026-07-10 (HedgeMatrix
// parity — "we invented something we don't need"); the collapsed-rail badge is the one
// surviving hint (owner-kept: the only signal WHY Run gates while a side is collapsed).
//
// Strike pickers are the platform ets-strikes-dropdown (owner smoke 2026-07-10 — never
// plain number boxes), fed the NEAREST available expiration, DATE-INDEPENDENT of the
// questionnaire's chosen date(s) (the HedgeMatrix copilot's owner rule 2026-06-21).
@Component({
    selector: 'ets-opg-side-questionnaire',
    templateUrl: './opg-side-questionnaire.component.html',
    styleUrls: ['./opg-side-questionnaire.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpgSideQuestionnaireComponent {

    constructor(private _changeDetector: ChangeDetectorRef) {
    }

    @Input() side: OpgSide;
    @Input() presetUnderlying: string;
    @Input() presetApgSource: string = null;

    get presetAnchorInvalid(): boolean {
        return this.inputs != null && this.inputs.mode === 'preset' && this.inputs.presetAnchor != null
            && !isPresetAnchorValid(this.inputs.presetAnchor);
    }

    @DetectMethodChanges()
    onPresetChanged(): void {
        if (this.inputs && this.inputs.mode === 'preset' && this.inputs.presetAnchor == null) {
            this.inputs.presetAnchor = this.chosenAtmValue;
        }
        this.changed.emit();
    }

    @DetectMethodChanges()
    onPresetAnchorChanged(value: number): void {
        this.inputs.presetAnchor = value;
        this.changed.emit();
    }

    private _inputs: SideInputs;

    // Hydrates the local UI mirror from the parent-owned working object (e.g. tab switch,
    // session reset). Assign-only + detectChanges; emits NOTHING (not user-driven).
    @Input()
    @DetectSetterChanges()
    set inputs(value: SideInputs) {
        this._inputs = value;
        this.hydrateFromInputs();
    }

    get inputs(): SideInputs {
        return this._inputs;
    }

    // Emitted after EVERY user-driven mutation of `inputs` (never from setters/hydration).
    @Output() changed = new EventEmitter<void>();

    // The strike list the pickers offer AND the 'strikes' mode membership validation runs
    // against — the SAME list by construction (picker-validator parity, A3). The component
    // never filters or reshapes anything with it (courier §6).
    @Input() strikeLadder: number[] = [];

    // Feed the ets-strikes-dropdown pickers: the panel's instrument + the NEAREST available
    // expiration (date-independent — the HedgeMatrix copilot's owner rule 2026-06-21). The
    // dropdown self-disables while either is missing (its own isDisabled).
    @Input() targetInstrument: TradingInstrument | null = null;

    @Input() strikeExpiration: OptionExpirationDescriptor | null = null;

    // The Chosen ATM strike VALUE shown on the "Chosen ATM · 7500" pill (WI-4). Fed from the
    // orchestrator's facade read; re-read on every CD (live ticking not required — the pill just
    // shows the current value). null = not yet known. Market ATM shows NO value (owner 2026-07-13
    // — it is dynamic and not worth maintaining), so no market-value input exists here.
    @Input() chosenAtmValue: number | null = null;

    @Input() collapsible = false;

    // Locked while a run is in flight (§6 courier: the sent questionnaire is a snapshot —
    // mid-flight edits must be impossible, not just unrecorded). Collapse stays allowed
    // (visual only). Bound by the orchestrator to the single-flight state.
    @Input() disabled = false;

    private _collapsed = false;

    // Two-way sink: assign-only. NEVER emits collapsedChange from here (echo-loop hazard);
    // the emit lives in onToggleCollapsed (user click).
    @Input()
    @DetectSetterChanges()
    set collapsed(value: boolean) {
        this._collapsed = value;
    }

    get collapsed(): boolean {
        return this._collapsed;
    }

    @Output() collapsedChange = new EventEmitter<boolean>();

    // ---- Segmented button sets (dx-button-group renders item.text, keys on id; per-button
    // ---- data-element via item.elementAttr — sketch ID starter set) ----------------------

    // CR-20260727-001. The Expected Range pill is DISABLED IN PLACE when the user's Expected Move
    // setting cannot produce a range — never filtered out (owner ruling 2026-07-27 R2: "I don't
    // want it to be disappeared from the price range of choices, I want it to be a disabled
    // state"). A pill that vanishes reflows the group and hides the fact that the mode exists at
    // all; a greyed one keeps the geometry stable and says "not now" instead of "never was".
    // dx-button-group items support per-item `disabled` + `hint` (dxButtonGroupItem extends
    // CollectionWidgetItem), so this needs no widget change.
    private readonly _priceRangeButtonLabels: { id: 'strikes' | 'followThrough' | 'atmOffset' | 'expectedRange'; text: string; elementAttr: { [key: string]: string } }[] = [
        // 'Range' (not 'Strikes Range') — HedgeMatrix parity, owner smoke 2026-07-10.
        {id: 'strikes', text: 'Range', elementAttr: {'data-element': 'btn-pr-strikes'}},
        {id: 'followThrough', text: 'Follow-Through', elementAttr: {'data-element': 'btn-pr-follow'}},
        {id: 'atmOffset', text: 'ATM Offset', elementAttr: {'data-element': 'btn-pr-atm-offset'}},
        // CR-20260715-001: fourth choice, AFTER ATM Offset (owner-placed, meeting 2026-07-15).
        // Label matches the grid header's "Expected Range" readout (G1 Q2).
        {id: 'expectedRange', text: 'Expected Range', elementAttr: {'data-element': 'btn-pr-expected-range'}}
    ];

    // Why the disabled controls carry a hint: greyed-out alone answers "can I click this" but not
    // "why not", and the cause lives in a different dialog entirely (Application Settings), which
    // is not somewhere a user would think to look from here.
    static readonly EXPECTED_RANGE_UNAVAILABLE_HINT =
        'Enable Expected Move Settings in Application Settings to Use the Expected Range.';

    // Memoized on the availability boolean, the atmAnchorButtons idiom: dx-button-group must keep a
    // STABLE array reference between renders or it rebuilds its items every change-detection pass.
    // Sentinel `undefined` forces the first build.
    private _expectedRangeAvailableKey: boolean | undefined = undefined;
    private _priceRangeButtonsCache: { id: string; text: string; disabled?: boolean; hint?: string; elementAttr: { [key: string]: string } }[] = [];

    get priceRangeButtons(): { id: string; text: string; disabled?: boolean; hint?: string; elementAttr: { [key: string]: string } }[] {
        const available = this.expectedRangeAvailable;
        if (available !== this._expectedRangeAvailableKey) {
            this._expectedRangeAvailableKey = available;
            this._priceRangeButtonsCache = this._priceRangeButtonLabels.map(b =>
                b.id === 'expectedRange' && !available
                    ? {
                        ...b,
                        disabled: true,
                        hint: OpgSideQuestionnaireComponent.EXPECTED_RANGE_UNAVAILABLE_HINT
                    }
                    : {...b});
        }
        return this._priceRangeButtonsCache;
    }

    /**
     * CR-20260727-001. Can the user's Expected Move setting currently produce a range (owner
     * rulings D2/D3)? Gates BOTH expected-range entry points — this pill and the "Use Expected
     * Range" checkbox under ATM Offset's bounded subs. They reduce to the same backend boundary
     * math, so they must grey out together; gating only one would leave the other as a live route
     * to a mode the settings say is unavailable.
     *
     * PUSHED DOWN AS AN INPUT, not read from the service here. This component is OnPush, and a
     * settings change is not an event in its own subtree — reading the service from a getter meant
     * the pill only re-evaluated when something ELSE happened to check this component, so the gate
     * visibly lagged until the user clicked it (owner report 2026-07-27 R1: "it kind of awaits for
     * a refresh"). An input changes by reference and wakes the child on its own. It also keeps the
     * predicate in ONE place for all four instantiations, so two questionnaires cannot disagree.
     */
    @Input() expectedRangeAvailable = true;

    // CR-20260831-004: this side's changed-vs-last-run fields, pre-sliced by the orchestrator
    // (side prefix stripped — keys like 'priceRange.kind', 'minProfit.amount'; the grammar lives
    // in opg-copilot-param-diff.ts). A FRESH object per recompute is the OnPush wake-up. Null/empty
    // = nothing changed (or no run yet) — every cue off.
    @Input() changedKeys: ParamChangeMap | null = null;

    // CR-20260901-001: the portfolio's default quantity, read by the orchestrator off the host
    // facade at every CD pass — the mode-flip handlers convert the typed amount with the value
    // current AT THE FLIP (owner ruling). Null/unknown qty = no conversion (the number is left
    // saying what it said; the engine-side skip reasons already handle a missing qty on Raw).
    @Input() defaultQty: number | null = null;

    // Template guard for the per-control [class.param-changed] bindings.
    isChanged(key: string): boolean {
        return !!(this.changedKeys && this.changedKeys[key]);
    }

    // Template-reachable alias for the static above (a static is not addressable from a template).
    // One string, two controls — the pill's hint and the checkbox's must not drift apart.
    get expectedRangeUnavailableHint(): string {
        return OpgSideQuestionnaireComponent.EXPECTED_RANGE_UNAVAILABLE_HINT;
    }

    // ---- rebuild-survivable dx-button-group selection (CR-20260831-004 defect round 2) --------
    // The price-range and ATM-anchor groups REBUILD their items (availability gate / chosen-ATM
    // value ticks), and on every items rebuild ButtonGroup re-feeds its stale self-synced
    // `selectedItems` object list into the fresh button collection — fighting [selectedItemKeys]
    // and producing the owner-reported mutual-exclusivity bug (two segments lit at once, or none
    // while one is selected). Binding [selectedItems] to the CURRENT items array's selected object
    // makes the two sources agree by construction. Memoized per group on (items reference,
    // selected id) so the OnPush bindings stay reference-stable between real changes.
    private _selCache: { [group: string]: { itemsRef: any; id: any; value: any[] } } = {};

    private selectedItemsFor<T extends { id: any }>(group: string, items: T[], id: any): T[] {
        const hit = this._selCache[group];
        if (hit && hit.itemsRef === items && hit.id === id) {
            return hit.value;
        }
        const value = items.filter(b => b.id === id);
        this._selCache[group] = { itemsRef: items, id: id, value: value };
        return value;
    }

    get priceRangeSelectedItems(): { id: string; text: string; disabled?: boolean; hint?: string; elementAttr: { [key: string]: string } }[] {
        return this.selectedItemsFor('priceKind', this.priceRangeButtons, this.priceKind);
    }

    get atmAnchorSelectedItems(): { id: OpgAtmAnchor; text: string; elementAttr: { [key: string]: string } }[] {
        return this.selectedItemsFor('atmAnchor', this.atmAnchorButtons, this.atmAnchor);
    }

    // (CR-20260727-002: the Follow-Through `directionButtons` — Up | Down — are GONE. The
    // direction is the SIDE, so the pair offered a choice that does not exist and let a puts side
    // declare it ran upward. The row now carries followThroughStatement below instead.)

    // WI-4 secondary "off that strike" choice under ATM Offset: a bounded Range, an open-ended
    // Follow-Through, or Both (Both CR — range AND follow-through candidates from one run).
    // Both replaces CR-20260715-001's "Expected Range" button: the expected range is not a
    // coverage shape but the DYNAMIC way of sizing a range — it moved into the bounded subs'
    // "Use Expected Range" checkbox below. (The atmOffset ANCHOR buttons are a memoized getter
    // that carries the live strike VALUES — see atmAnchorButtons below.)
    readonly atmSubKindButtons: { id: 'range' | 'followThrough' | 'both'; text: string; elementAttr: { [key: string]: string } }[] = [
        {id: 'range', text: 'Range', elementAttr: {'data-element': 'btn-atm-sub-range'}},
        {id: 'followThrough', text: 'Follow-Through', elementAttr: {'data-element': 'btn-atm-sub-follow'}},
        {id: 'both', text: 'Both', elementAttr: {'data-element': 'btn-atm-sub-both'}}
    ];

    // Minimum Profit input MODE (vrequest 2026-07-20) — replaced a "Normalized" checkbox. The two
    // modes are mutually exclusive and change what the SAME typed number means: 'normalized' = a
    // PER-UNIT amount (W_pts = amount); 'raw' = the position-TOTAL dollars (W_pts = amount /
    // (qty * 100)) — see OpgRunNormalizer.NormalizeSide. A checkbox left the unchecked meaning
    // unwritten; naming both sides is the point of the change.
    readonly minProfitModeButtons: { id: 'normalized' | 'raw'; text: string; elementAttr: { [key: string]: string } }[] = [
        {id: 'normalized', text: 'Normalized (Def. Qty)', elementAttr: {'data-element': 'btn-min-profit-normalized'}},
        {id: 'raw', text: 'Raw ($)', elementAttr: {'data-element': 'btn-min-profit-raw'}}
    ];

    readonly maxRiskButtons: { id: 'dollar' | 'relative'; text: string; elementAttr: { [key: string]: string } }[] = [
        {id: 'dollar', text: 'Dollar ($)', elementAttr: {'data-element': 'btn-risk-dollar'}},
        {id: 'relative', text: 'Relative', elementAttr: {'data-element': 'btn-risk-relative'}}
    ];

    // Derived from the domain constant so the §10 label rule cannot drift:
    // Low | Moderate | Aggressive | All — btn-risk-low/-moderate/-aggressive/-all.
    readonly relativeLevelButtons: { id: OpgRelativeRiskLevel; text: string; elementAttr: { [key: string]: string } }[] =
        OPG_RELATIVE_RISK_LEVELS.map(l => ({id: l, text: l, elementAttr: {'data-element': 'btn-risk-' + l.toLowerCase()}}));

    // ---- Local UI mirror (Hedge donor idiom): the widgets bind here; user-driven handlers
    // ---- write through into the parent-owned `inputs` object ----------------------------

    priceKind: 'strikes' | 'followThrough' | 'atmOffset' | 'expectedRange' = 'strikes';
    startStrike: number | null = null;      // shared by 'strikes', 'followThrough' and 'expectedRange' (donor idiom)
    endStrike: number | null = null;
    // (CR-20260727-002: no `direction` mirror — Follow-Through takes no direction input.)
    atmAnchor: OpgAtmAnchor = 'chosen';     // A3 — 'chosen' is the §5 default
    atmOffset: number | null = null;                     // WI-4 — ONE signed start offset off the anchor
    atmSubKind: 'range' | 'followThrough' | 'both' = 'range';   // WI-4 — secondary "off that strike" choice (+ Both, Both CR)
    atmExtent: number | null = null;                     // WI-4 — the bounded subs' static $ extent (swept in the side's OTM direction). CLEARED when "Use Expected Range" is checked (owner smoke 2026-07-15) — un-checking re-enables an EMPTY box, so the Run stays gated until a fresh number is typed
    atmUseExpectedRange = false;                         // Both CR — the bounded subs' dynamic choice: checked = the skill's expected range sizes the range family; blocks + clears the $ box

    minProfitAmount: number | null = null;
    // Normalized by DEFAULT (vrequest 2026-07-20). NOTE this initializer is NOT the effective
    // default: hydrateFromInputs() overwrites it from ins.minProfit.normalized on every hydration
    // (guarded only by `ins.minProfit != null`, which the seed always satisfies), so the
    // real seed is makeDefaultSideInputs() in opg-copilot.component.ts. Kept in agreement with it
    // deliberately — if the two ever disagree the panel paints one mode and reports the other for
    // the instant before the first sync.
    minProfitNormalized = true;

    maxRiskKind: 'dollar' | 'relative' = 'dollar';
    dollarAmount: number | null = null;
    // Normalized by DEFAULT (vrequest 2026-07-20). Unlike minProfitNormalized above, THIS one IS
    // the effective default: hydrateFromInputs() only writes it inside the mr.kind === 'dollar' branch,
    // and the default side seeds maxRisk as {kind:'relative'} — so that branch never runs at init
    // and this value survives to the first render.
    dollarNormalized = true;
    relativeLevel: OpgRelativeRiskLevel = 'Low';

    // Presentation adapter for the Minimum Profit segmented selector. The state of record stays
    // the BOOLEAN above (the wire field is a boolean and is unchanged) — this getter exists so the
    // template binds a key, not a ternary, and so there is no second source of truth to drift.
    get minProfitMode(): 'normalized' | 'raw' {
        return this.minProfitNormalized ? 'normalized' : 'raw';
    }

    // ---- Presentation ---------------------------------------------------------------------

    get sideName(): string {
        return this.side === 'puts' ? 'Puts' : 'Calls';
    }

    get isCollapsed(): boolean {
        return this.collapsible && this._collapsed;
    }

    // Feeds ONLY the collapsed-rail badge (the per-block legend hints were removed at owner
    // smoke 2026-07-10; this badge was owner-KEPT — the one signal WHY Run gates while the
    // side is collapsed). Run gating itself lives in the orchestrator's isTabComplete.
    get isComplete(): boolean {
        return isSideComplete(this._inputs, this.strikeLadder || [], this.side);
    }

    // ---- ATM Offset numeric inputs (WI-4 smoke revision, owner 2026-07-13) ------------------

    // Both the offset and range boxes spin/type in multiples of the underlying's strike step
    // (SPX 5, default 1) — bound to each dx-number-box [step], and the edit handlers snap manual
    // entry to it. Cheap facade-derived read; fine to call per CD.
    get strikeStep(): number {
        return getStrikeStep(this.targetInstrument ? this.targetInstrument.ticker : null);
    }

    // The anchor pills: ONLY Chosen shows its strike value ("Chosen ATM · 7500"); Market ATM is
    // the bare label (owner 2026-07-13 — the market value is dynamic, not worth maintaining).
    // Memoized on the chosen value so the dx-button-group items keep a stable reference until it
    // moves. Sentinel `undefined` (distinct from a real null value) forces the first build.
    private _atmAnchorKey: number | null | undefined = undefined;
    private _atmAnchorButtonsCache: { id: OpgAtmAnchor; text: string; elementAttr: { [key: string]: string } }[] = [];

    get atmAnchorButtons(): { id: OpgAtmAnchor; text: string; elementAttr: { [key: string]: string } }[] {
        if (this.chosenAtmValue !== this._atmAnchorKey) {
            this._atmAnchorKey = this.chosenAtmValue;
            this._atmAnchorButtonsCache = [
                {id: 'chosen', text: anchorPillText('Chosen ATM', this.chosenAtmValue), elementAttr: {'data-element': 'btn-atm-anchor-chosen'}},
                {id: 'market', text: 'Market ATM', elementAttr: {'data-element': 'btn-atm-anchor-market'}}
            ];
        }
        return this._atmAnchorButtonsCache;
    }

    // The Follow-Through MAIN mode's statement (CR-20260727-002), replacing the Up | Down
    // selector: "from 7540 and up ↑" on calls, "from 7480 and down ↓" on puts.
    //
    // Wording per the owner's second sketch (2026-07-27, revising the first): a SENTENCE with the
    // strike inside it, not a strike followed by labels. Lower-case "from"/"up"/"down" and the
    // trailing arrow are the sketch's, verbatim.
    //
    // TWO GETTERS, TWO CONTROLS — do NOT merge this with followThroughText below, and note the
    // revised wording brings the two strings CLOSER, which makes the distinction easier to lose:
    //   this one          -> the main followThrough mode's Start Strike ROW; a bordered statement
    //                        box; names the CHOSEN STRIKE ("from 7540 and up ↑").
    //   followThroughText -> the ATM Offset "Distance To Cover" sub's muted HINT line; names the
    //                        ANCHORED ATM, never a strike ("From ATM and up"), because that mode's
    //                        start is the anchor pill's value, not a picked strike.
    // Different rows, different sources, different punctuation. Merging them would put a strike in
    // a place that has none, or drop the strike from the place that needs it.
    get followThroughStatement(): string {
        return followThroughSentence(this.startStrike, this.side === 'puts' ? 'Down ↓' : 'up ↑');
    }

    // Follow-Through takes no numeric input — just a side-aware line of the coverage (owner
    // sketch 2026-07-13): calls sweep UP from the ATM, puts sweep DOWN.
    // This is the ATM OFFSET SUB's hint, not the main mode's statement — see
    // followThroughStatement above before touching either.
    get followThroughText(): string {
        return this.side === 'puts' ? 'From ATM And Down' : 'From ATM And up';
    }

    // Expected Range main mode (CR-20260715-001): the start strike is the ONLY input — this
    // line says where the coverage runs, side-aware like followThroughText. The range end is
    // per-date and the SKILL's own calculation (never shown or asked for here).
    get expectedRangeText(): string {
        return this.side === 'puts'
            ? 'From the Start Strike Down to the Expected Range'
            : 'From the Start Strike up to the Expected Range';
    }

    // The bounded subs (Range | Both) share one value block: label + $ box + checkbox.
    get atmSubNeedsRangeValue(): boolean {
        return this.atmSubKind === 'range' || this.atmSubKind === 'both';
    }

    // Coverage line under the checked "Use Expected Range" box (Both CR — the dynamic value):
    // says where the range family runs, side-aware; the per-date end is the skill's calculation.
    get atmExpectedRangeText(): string {
        return this.side === 'puts' ? 'Down to the Expected Range' : 'Up to the Expected Range';
    }

    // Coverage line for the Both sub (Both CR): one run returns BOTH families off the same
    // offset start — the bounded range plus the open-ended follow-through, side-aware.
    get atmBothText(): string {
        return this.side === 'puts'
            ? 'Range + Follow-Through Candidates, Both From the Start And Down'
            : 'Range + Follow-Through Candidates, Both From the Start And up';
    }

    // Whether the Max Risk question exists for this side's coverage (hide-risk-for-
    // follow-through, meeting 2026-07-15 pkg-03, owner 00:43–00:44): a follow-through-only
    // side hides the WHOLE fieldset — removal, never a greyed-out state. Reads the MODEL
    // (not the local mirror) so the same predicate that gates the Run and guards the write
    // drives the visibility — the three can never disagree. Model and mirror agree at every
    // decorated CD pass: the flip handlers write the model before their decorator flushes.
    get maxRiskVisible(): boolean {
        return !isFollowThroughOnly(this._inputs ? this._inputs.priceRange : null);
    }

    // Risk-scoping note while Both is selected (Both CR, owner 00:28: risk levels apply "only
    // to the applicable trade such as a range"): shown under the RELATIVE risk levels only —
    // a dollar cap is a filter every trade has, so it needs no note.
    get bothRiskScopeVisible(): boolean {
        return this.priceKind === 'atmOffset' && this.atmSubKind === 'both' && this.maxRiskKind === 'relative';
    }

    readonly bothRiskScopeHint = 'Shapes Range Trades Only — Follow-Throughs Carry no Risk Lever';

    // Sign meaning shown under the offset box (owner 2026-07-13): a negative offset is
    // IN-the-money, a positive one is OUT-of-the-money (the call/put side resolves direction).
    readonly atmOffsetSignHint = '(' + MINUS_SIGN + ') Means "in-the-Money", (+) Means "Out-of-the-Money"';

    // ---- User-driven handlers (the ONLY places that mutate `inputs` + emit `changed`) ------

    // Collapse toggle (header bar / rail click). Visual only — inputs are NEVER cleared.
    @DetectMethodChanges()
    onToggleCollapsed(): void {
        if (!this.collapsible) {
            return;
        }
        this._collapsed = !this._collapsed;
        this.collapsedChange.emit(this._collapsed);
    }

    // Mode switch REPLACES inputs.priceRange with a fresh variant object (discriminated union).
    // One of the two handlers that can flip follow-through-effectiveness — the maxRisk
    // re-write AFTER the priceRange write keeps the null-when-follow-through invariant
    // (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03).
    @DetectMethodChanges()
    onPriceKind(e: { itemData: { id: 'strikes' | 'followThrough' | 'atmOffset' | 'expectedRange' } }): void {
        if (this.disabled) {
            return;
        }
        // CR-20260727-001. dx-button-group already swallows clicks on a disabled item, so this is
        // belt-and-braces — but a mode flip WRITES the model, and an unavailable mode written into
        // workingInputs is exactly the state the Run gate exists to prevent. Cheap guard, matches
        // the `disabled` early-return above.
        if (e.itemData.id === 'expectedRange' && !this.expectedRangeAvailable) {
            return;
        }
        this.priceKind = e.itemData.id;
        this.writePriceRange();
        this.writeMaxRisk();
        this.changed.emit();
    }

    // (CR-20260727-002: onDirection is GONE with the Up | Down selector — Follow-Through has no
    // direction question. The followThroughStatement getter ABOVE states the coverage instead;
    // being a getter it needs no handler, which is why nothing replaces this method.)

    @DetectMethodChanges()
    onAtmAnchor(e: { itemData: { id: OpgAtmAnchor } }): void {
        if (this.disabled) {
            return;
        }
        this.atmAnchor = e.itemData.id;
        this.writePriceRange();
        this.changed.emit();
    }

    // WI-4 secondary "off that strike" choice: Range | Follow-Through | Both (Both CR — the
    // bounded kinds carry the range value block; Follow-Through stays input-free).
    // The other follow-through-effectiveness flip point — same write order as onPriceKind
    // (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03).
    @DetectMethodChanges()
    onAtmSubKind(e: { itemData: { id: 'range' | 'followThrough' | 'both' } }): void {
        if (this.disabled) {
            return;
        }
        this.atmSubKind = e.itemData.id;
        this.writePriceRange();
        this.writeMaxRisk();
        this.changed.emit();
    }

    // The "Use Expected Range" checkbox (Both CR): flips the bounded subs' value between the
    // static $ extent and the dynamic expected range. [(value)] already moved
    // atmUseExpectedRange. Checking CLEARS the $ box (owner smoke 2026-07-15 — a number that
    // no longer travels must not sit in a disabled box); the programmatic null write-back
    // through [(value)] carries no $event.event, so onAtmExtentEdited ignores the echo. Same
    // $event.event echo guard as every DevExtreme handler here.
    @DetectMethodChanges()
    onAtmUseExpectedRangeEdited(e: { event?: any }): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        if (this.atmUseExpectedRange) {
            this.atmExtent = null;
        }
        this.writePriceRange();
        this.changed.emit();
    }

    // Strike pickers (ets-strikes-dropdown) route here. The dropdown's strikeChanged carries
    // no $event.event discriminator; its strike INPUT setter is assign-only (verified), so
    // hydration through [(strike)] never echoes back — the emits are user picks plus the
    // dropdown's own expiration-driven resets (a vanished strike nulls out, which must
    // write through so the Run re-gates).
    @DetectMethodChanges()
    onStrikePicked(): void {
        if (this.disabled) {
            return;
        }
        this.writePriceRange();
        this.changed.emit();
    }

    // The signed offset box: snap manual entry / spins to the NEAREST strike-step multiple
    // (owner: enforce typed input too), then write through. Programmatic syncs — hydration and
    // the snap write-back itself — carry no $event.event and are ignored, so there is no loop.
    @DetectMethodChanges()
    onAtmOffsetEdited(e: { event?: any }): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        this.atmOffset = snapToStrikeStep(this.atmOffset, this.strikeStep, false);
        this.writePriceRange();
        this.changed.emit();
    }

    // The range extent box: snap to a POSITIVE strike-step multiple (a range needs at least one
    // step), then write through. Same echo guard.
    @DetectMethodChanges()
    onAtmExtentEdited(e: { event?: any }): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        this.atmExtent = snapToStrikeStep(this.atmExtent, this.strikeStep, true);
        this.writePriceRange();
        this.changed.emit();
    }

    @DetectMethodChanges()
    onMinProfitEdited(e: { event?: any }): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        this.writeMinProfit();
        this.changed.emit();
    }

    // No `e.event` guard here, unlike onMinProfitEdited above: that guard exists because a
    // dx-number-box fires onValueChanged for PROGRAMMATIC writes too (hydrateFromInputs), and only a
    // user edit may write through. A dx-button-group's onItemClick is user-driven by definition,
    // so it follows onMaxRiskKind's shape instead.
    @DetectMethodChanges()
    onMinProfitMode(e: { itemData: { id: 'normalized' | 'raw' } }): void {
        if (this.disabled) {
            return;
        }
        const toNormalized = e.itemData.id === 'normalized';
        // CR-20260901-001: an actual FLIP converts the typed amount so it keeps meaning the same
        // thing (a re-click on the already-selected mode converts nothing — dx-button-group fires
        // onItemClick for those too).
        if (toNormalized !== this.minProfitNormalized) {
            this.minProfitAmount = convertOnNormalizedModeFlip(this.minProfitAmount, toNormalized, this.defaultQty);
        }
        this.minProfitNormalized = toNormalized;
        this.writeMinProfit();
        this.changed.emit();
    }

    @DetectMethodChanges()
    onMaxRiskKind(e: { itemData: { id: 'dollar' | 'relative' } }): void {
        if (this.disabled) {
            return;
        }
        this.maxRiskKind = e.itemData.id;
        this.writeMaxRisk();
        this.changed.emit();
    }

    @DetectMethodChanges()
    onRelativeLevel(e: { itemData: { id: OpgRelativeRiskLevel } }): void {
        if (this.disabled) {
            return;
        }
        this.relativeLevel = e.itemData.id;
        this.writeMaxRisk();
        this.changed.emit();
    }

    @DetectMethodChanges()
    onDollarRiskEdited(e: { event?: any }): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        this.writeMaxRisk();
        this.changed.emit();
    }

    // CR-20260901-001: the max-risk "Normalized" checkbox gets its OWN handler (it shared
    // onDollarRiskEdited before) because a mode flip must also CONVERT the typed amount —
    // [(value)] has already flipped dollarNormalized by the time this runs, so convert toward
    // the new state. Same $event.event echo guard as every DevExtreme handler here.
    @DetectMethodChanges()
    onDollarNormalizedEdited(e: { event?: any }): void {
        if (this.disabled || !e || !e.event) {
            return;
        }
        this.dollarAmount = convertOnNormalizedModeFlip(this.dollarAmount, this.dollarNormalized, this.defaultQty);
        this.writeMaxRisk();
        this.changed.emit();
    }

    // ---- Write-through into the parent-owned object -----------------------------------------

    private writePriceRange(): void {
        if (this._inputs == null) {
            return;
        }
        this._inputs.priceRange = this.buildPriceRange();
    }

    private buildPriceRange(): OpgPriceRange {
        if (this.priceKind === 'strikes') {
            return {kind: 'strikes', startStrike: orNaN(this.startStrike), endStrike: orNaN(this.endStrike)};
        }
        if (this.priceKind === 'followThrough') {
            // CR-20260727-002: the start strike is all that travels — the side carries the
            // direction, so there is nothing else to send.
            return {kind: 'followThrough', startStrike: orNaN(this.startStrike)};
        }
        if (this.priceKind === 'expectedRange') {
            // CR-20260715-001: the fact + the start strike — nothing else travels (courier).
            return {kind: 'expectedRange', startStrike: orNaN(this.startStrike)};
        }
        return {kind: 'atmOffset', anchor: this.atmAnchor, offset: orNaN(this.atmOffset), sub: this.buildAtmSub()};
    }

    private buildAtmSub(): OpgAtmOffsetSub {
        if (this.atmSubKind === 'range') {
            return {kind: 'range', value: this.buildAtmRangeValue()};
        }
        if (this.atmSubKind === 'both') {
            return {kind: 'both', value: this.buildAtmRangeValue()};
        }
        return {kind: 'followThrough'};
    }

    // The bounded subs' range value (Both CR): the checkbox decides static vs dynamic. The
    // dynamic variant deliberately carries NO number (courier — the skill calculates each
    // date's end), so a typed extent never leaks onto the wire while the box is checked.
    private buildAtmRangeValue(): OpgAtmRangeValue {
        if (this.atmUseExpectedRange) {
            return {mode: 'expectedRange'};
        }
        return {mode: 'dollar', extent: orNaN(this.atmExtent)};
    }

    private writeMinProfit(): void {
        if (this._inputs == null) {
            return;
        }
        if (this._inputs.minProfit == null) {
            this._inputs.minProfit = {amount: NaN, normalized: false};
        }
        this._inputs.minProfit.amount = orNaN(this.minProfitAmount);
        this._inputs.minProfit.normalized = this.minProfitNormalized;
    }

    // The ONE writer of inputs.maxRisk — it owns the invariant (hide-risk-for-follow-through,
    // meeting 2026-07-15 pkg-03): a follow-through-only side carries NULL (the question does
    // not exist), every other coverage carries the mirror's current value. The flip handlers
    // (onPriceKind/onAtmSubKind) call this AFTER writePriceRange so the guard reads the
    // just-written kind; leaving follow-through restores the remembered mirror choice. The
    // risk-edit handlers below are only reachable while the fieldset renders (non-FT), so
    // they always take the build branch.
    private writeMaxRisk(): void {
        if (this._inputs == null) {
            return;
        }
        this._inputs.maxRisk = isFollowThroughOnly(this._inputs.priceRange) ? null : this.buildMaxRisk();
    }

    private buildMaxRisk(): OpgMaxRisk {
        if (this.maxRiskKind === 'dollar') {
            return {kind: 'dollar', amount: orNaN(this.dollarAmount), normalized: this.dollarNormalized};
        }
        return {kind: 'relative', level: this.relativeLevel};
    }

    // ---- Hydration (parent object -> local UI mirror; never emits) ---------------------------

    private hydrateFromInputs(): void {
        const ins = this._inputs;
        if (ins == null) {
            return;
        }
        const pr = ins.priceRange;
        if (pr != null) {
            if (pr.kind === 'strikes') {
                this.priceKind = 'strikes';
                this.startStrike = orNull(pr.startStrike);
                this.endStrike = orNull(pr.endStrike);
            } else if (pr.kind === 'followThrough') {
                // CR-20260727-002: nothing but the strike to hydrate — the direction is the side,
                // and the side is an @Input, not part of the model being hydrated.
                this.priceKind = 'followThrough';
                this.startStrike = orNull(pr.startStrike);
            } else if (pr.kind === 'expectedRange') {
                this.priceKind = 'expectedRange';
                this.startStrike = orNull(pr.startStrike);
            } else if (pr.kind === 'atmOffset') {
                this.priceKind = 'atmOffset';
                this.atmAnchor = pr.anchor === 'market' ? 'market' : 'chosen';
                this.atmOffset = orNull(pr.offset);
                if (pr.sub && pr.sub.kind === 'followThrough') {
                    this.atmSubKind = 'followThrough';
                    this.atmExtent = null;
                    this.atmUseExpectedRange = false;
                } else if (pr.sub && (pr.sub.kind === 'range' || pr.sub.kind === 'both')) {
                    this.atmSubKind = pr.sub.kind;
                    const v = pr.sub.value;
                    this.atmUseExpectedRange = !!v && v.mode === 'expectedRange';
                    this.atmExtent = (v && v.mode === 'dollar') ? orNull(v.extent) : null;
                } else {
                    // Malformed/absent sub — the structurally-valid default (mirrors makeDefault).
                    this.atmSubKind = 'range';
                    this.atmExtent = null;
                    this.atmUseExpectedRange = false;
                }
            }
        }
        if (ins.minProfit != null) {
            this.minProfitAmount = orNull(ins.minProfit.amount);
            this.minProfitNormalized = !!ins.minProfit.normalized;
        }
        const mr = ins.maxRisk;
        if (mr != null) {
            if (mr.kind === 'dollar') {
                this.maxRiskKind = 'dollar';
                this.dollarAmount = orNull(mr.amount);
                this.dollarNormalized = !!mr.normalized;
            } else if (mr.kind === 'relative') {
                this.maxRiskKind = 'relative';
                if (OPG_RELATIVE_RISK_LEVELS.indexOf(mr.level) >= 0) {
                    this.relativeLevel = mr.level;
                }
            }
        }
    }
}

// Proper minus sign (U+2212), matching the breadcrumb + the WI-4 mockup (never a hyphen).
const MINUS_SIGN = '−';

// "Chosen ATM · 7500" when the value is known; the bare label otherwise (WI-4 anchor pills).
function anchorPillText(label: string, value: number | null): string {
    return (typeof value === 'number' && isFinite(value)) ? label + ' · ' + value : label;
}

// "from 7540 and up ↑" / "from 7480 and down ↓" (CR-20260727-002, owner sketch 2026-07-27).
//
// The strike sits INSIDE the sentence, which is why this is not the anchorPillText shape above: the
// pill can drop its value and still read ("Chosen ATM"), whereas dropping this one would leave
// "from and up". So with no strike picked the words "the start strike" stand in — the SAME idiom
// expectedRangeText already uses for exactly this situation ("From the start strike down to the
// expected range"). That keeps the owner's Q2 ruling — the box never shows a bracketed placeholder,
// an em-dash, or an empty gap, and never disappears — while staying grammatical.
//
// Guards FINITENESS, not truthiness: an unset strike reaches the model as NaN via orNaN below, and
// String(NaN) would paint a literal "from NaN and up".
function followThroughSentence(strike: number | null, tail: string): string {
    const subject = (typeof strike === 'number' && isFinite(strike)) ? String(strike) : 'the Start Strike';
    return 'From ' + subject + ' And ' + tail;
}

// Domain numbers are `number`; the widgets want null for "empty". NaN survives the round
// trip as "invalid" (isFiniteNumber gates it) — the Hedge donor's orNaN idiom, both ways.
function orNaN(n: number | null): number {
    return n == null ? NaN : n;
}

function orNull(n: number): number | null {
    return typeof n === 'number' && isFinite(n) ? n : null;
}
